import React, { useEffect, useState } from "react";

//UI Material
import {
  Button,
  Card,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

//Material Icons
import { Add, AddRounded, Edit, Remove, Shuffle } from "@mui/icons-material";

//Helpers
import { selectProductImage } from "../../Helpers/productSelector";

//Local components
import Selector from "../basics/Selector";
import { categoriesDict } from "../../Constants/products";
import ElementCard from "../../ProComponents/ElementCard";

export default function ProductToQuote({
  data = [],
  setData,
  setCurrency,
  currency,
  products = [],
}) {
  // ==========> Component States <==========
  //State for the selected product to quote
  const [productToAdd, setProductToAdd] = useState(0);
  //State for the quatity of product
  const [quantity, setQuantity] = useState(1);
  //State for details
  const [details, setDetails] = useState(products[productToAdd].details);
  //State for change the currency
  const [currencyToUSD, setCurrencyToUSD] = useState(currency === "USD");
  //State for the updated price for product
  const [price, setPrice] = useState(
    currencyToUSD
      ? products[productToAdd].priceInUSD
      : products[productToAdd].priceInCRC
  );
  //State for discount value
  const [discount, setDiscount] = useState(0);
  //State for selected category
  const [selectedCategory, setSelectedCategory] = useState(0);
  const [filteredProducts, setFilteredProducts] = useState(products);

  // ==========> Component Functions<==========
  //Function to generate unique id for each product added to the quote
  const generateId = () => {
    const timestamp = new Date().getTime();
    const randomNum = Math.floor(Math.random() * 10000);
    const uniqueId = `${timestamp}-${randomNum}`;
    return uniqueId;
  };

  /**
   * Function to add a selected product to quote summary
   */
  const addProductToQuote = () => {
    const dataCopy = [...data];
    dataCopy.push({
      id: generateId(),
      productName: filteredProducts[productToAdd].productName,
      specifications: details,
      quantity: quantity,
      price: Number(price - price * (discount / 100)).toFixed(2),
      discountPercentage: discount,
    });
    setData(dataCopy);
    setCurrency(currencyToUSD ? "USD" : "CRC");
  };

  /**
   * Function to remove items from products
   * @returns the reduced quantity of the selected product
   */
  const reduceQuantity = () => {
    if (quantity === 1) {
      return null;
    } else {
      return setQuantity(quantity - 1);
    }
  };

  useEffect(() => {
    const newFilteredProducts = products
      .filter((product) => {
        if (categoriesDict[selectedCategory].name === "ALL") {
          return products;
        } else {
          return product.type === categoriesDict[selectedCategory].name;
        }
      })
      .sort((a, b) => a.productName.localeCompare(b.productName));
    setFilteredProducts(newFilteredProducts);
    setProductToAdd(0);
    setPrice(
      currencyToUSD
        ? newFilteredProducts[0].priceInUSD
        : newFilteredProducts[0].priceInCRC
    );
    setQuantity(1);
    setDetails(newFilteredProducts[0].details);
    setDiscount(0);
  }, [selectedCategory]);

  // ==========> Component <==========
  return (
    <Card raised>
      <Grid container justifyContent="center" spacing={2} sx={{ padding: 2 }}>
        <Grid item xs={12}>
          <ElementCard
            ops={{
              title: "Información del producto",
              details: [
                "Seleccione un producto e ingrese las opciones que desea para la cotización.",
              ],
            }}
          />
        </Grid>

        <Grid item xs={12} align="center">
          <img
            src={selectProductImage(filteredProducts[productToAdd].productName)}
            alt={"Imagen del producto a cotizar"}
            height="150px"
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Stack justifyContent="center" spacing={1}>
            <Typography fontWeight="bold">Categorías</Typography>
            <Selector
              content={categoriesDict.map((cat) => cat.description)}
              value={selectedCategory}
              handleChange={(e) => {
                setSelectedCategory(e.target.value);
              }}
              width="100%"
            />
          </Stack>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Stack justifyContent="center" spacing={1}>
            <Typography fontWeight="bold">Nombre de ítem</Typography>
            <Selector
              content={filteredProducts.map((product) => product.productName)}
              value={productToAdd}
              handleChange={(e) => {
                setProductToAdd(e.target.value);
                setPrice(
                  currencyToUSD
                    ? filteredProducts[e.target.value].priceInUSD
                    : filteredProducts[e.target.value].priceInCRC
                );
                setQuantity(1);
                setDetails(filteredProducts[e.target.value].details);
                setDiscount(0);
              }}
              width="100%"
            />
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={1}
            >
              <IconButton
                onClick={() => {
                  reduceQuantity();
                }}
              >
                <Remove color="secondary" />
              </IconButton>
              <TextField
                type="number"
                value={quantity}
                onChange={(e) => {
                  const inputValue = e.target.value;
                  if (inputValue === "") {
                    setQuantity(1);
                    return;
                  }
                  const value = parseInt(inputValue);
                  if (!isNaN(value) && value >= 1) {
                    setQuantity(value);
                  }
                }}
                onBlur={(e) => {
                  if (e.target.value === "" || parseInt(e.target.value) < 1) {
                    setQuantity(1);
                  }
                }}
                inputProps={{
                  min: 1,
                  style: { textAlign: "center" },
                }}
                sx={{
                  width: "80px",
                  "& .MuiOutlinedInput-root": {
                    "& input": {
                      textAlign: "center",
                    },
                  },
                }}
              />
              <IconButton
                onClick={(e) => {
                  setQuantity(quantity + 1);
                }}
              >
                <AddRounded color="secondary" />
              </IconButton>
            </Stack>
          </Stack>
        </Grid>

        <Grid item xs={12}>
          <Stack justifyContent="center" spacing={1}>
            <Typography fontWeight="bold">Detalles del producto</Typography>
            <TextField
              value={details}
              onChange={(e) => {
                setDetails(e.target.value);
              }}
              multiline
              width="100%"
            />
          </Stack>
        </Grid>

        <Grid item xs={12}>
          <Stack justifyContent="center" spacing={1}>
            <Typography fontWeight="bold">Precio del producto</Typography>

            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ width: "100%" }}
            >
              <IconButton
                size="small"
                disabled={data.length > 0}
                onClick={() => {
                  setCurrencyToUSD(!currencyToUSD);
                  setPrice(
                    !currencyToUSD
                      ? filteredProducts[productToAdd].priceInUSD
                      : filteredProducts[productToAdd].priceInCRC
                  );
                }}
              >
                <Shuffle color="secondary" />
              </IconButton>
              <Stack>
                <Typography fontWeight={currencyToUSD ? null : "bold"}>
                  CRC
                </Typography>
                <Typography fontWeight={currencyToUSD ? "bold" : null}>
                  USD
                </Typography>
              </Stack>
              <TextField
                label="Precio unitario (sin IVA)"
                color="secondary"
                type="number"
                fullWidth
                value={Number(price).toFixed(2)}
                onChange={(e) => {
                  setPrice(e.target.value);
                }}
              />
              <TextField
                label="Desc(%)"
                color="secondary"
                type="number"
                value={discount}
                onChange={(e) => {
                  setDiscount(e.target.value);
                }}
              />
            </Stack>
          </Stack>
        </Grid>

        <Grid item xs={12} align="center">
          <Typography variant="h6" fontWeight="bold">
            {`${currencyToUSD ? "$" : "₡"}${Number(
              (price - price * (discount / 100)) * quantity
            ).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Stack direction="row" spacing={1} justifyContent="space-between">
            <Button
              variant="outlined"
              sx={{ color: "secondary" }}
              startIcon={<Edit />}
              onClick={() => {
                window.location.href = "/products";
              }}
            >
              {"Editar lista de productos"}
            </Button>
            <Button onClick={addProductToQuote} endIcon={<Add />}>
              AGREGAR PRODUCTO
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </Card>
  );
}
